import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import PyrotechnicBackground from '../components/PyrotechnicBackground';
import EnhancedHeroEffects from '../components/EnhancedHeroEffects';
import InteractiveBackground from '../components/InteractiveBackground';
import { useMousePosition } from '../hooks/useMousePosition';
import ReliabotixLogo from '../components/ReliabotixLogo';

const { FiZap, FiDollarSign, FiShield, FiMessageSquare, FiBarChart3, FiFileText, FiCode, FiArrowRight, FiPlay, FiCalendar, FiGithub, FiStar, FiTrendingUp, FiUsers, FiClock, FiTarget, FiCpu, FiLayers, FiX } = FiIcons;

const Home = () => {
  const [heroRef, heroInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [pillarsRef, pillarsInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [useCasesRef, useCasesInView] = useInView({ triggerOnce: true, threshold: 0.1 });
  const [showCalendar, setShowCalendar] = useState(false);
  const mousePosition = useMousePosition();

  const handleBookCall = (e) => {
    e.preventDefault();
    setShowCalendar(true);
  };

  const closeCalendar = () => {
    setShowCalendar(false);
  };

  const valuePillars = [
    {
      icon: FiZap,
      title: 'Lightning Fast',
      description: 'Reliabotix accelerates development from months to weeks with intelligent automation',
      proof: 'Average deployment: 2-4 weeks',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: FiDollarSign,
      title: 'Cost Optimized',
      description: 'Smart resource allocation and automated processes reduce operational costs significantly',
      proof: '75% cost reduction vs traditional methods',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: FiShield,
      title: 'Enterprise Ready',
      description: 'Bank-grade security, compliance frameworks, and scalable architecture from day one',
      proof: '100% SOC 2 compliant deployments',
      color: 'from-blue-500 to-cyan-500'
    }
  ];

  const useCases = [
    {
      icon: FiMessageSquare,
      title: 'Intelligent Support Systems',
      description: 'AI-powered customer service with natural language processing',
      metric: '60% faster response times',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiBarChart3,
      title: 'Predictive Analytics',
      description: 'Real-time insights and forecasting for data-driven decisions',
      metric: '45% improved accuracy',
      gradient: 'from-blue-500 to-indigo-500'
    },
    {
      icon: FiFileText,
      title: 'Document Intelligence',
      description: 'Automated processing and extraction from complex documents',
      metric: '85% processing time saved',
      gradient: 'from-green-500 to-teal-500'
    },
    {
      icon: FiCode,
      title: 'Workflow Automation',
      description: 'End-to-end process automation with intelligent orchestration',
      metric: '70% efficiency increase',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  return (
    <div className="relative overflow-hidden">
      {/* Calendar Modal */}
      <AnimatePresence>
        {showCalendar && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
            onClick={closeCalendar}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-4xl mx-4 max-h-[90vh] bg-dark-900/95 backdrop-blur-xl rounded-3xl border border-dark-700/50 overflow-hidden shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-dark-700/30">
                <div className="flex items-center">
                  <ReliabotixLogo size={40} variant="minimal" animate={false} />
                  <div className="ml-3">
                    <h3 className="text-xl font-bold text-white">Schedule Discovery Call</h3>
                    <p className="text-gray-400 text-sm">Free 30-minute consultation</p>
                  </div>
                </div>
                <button
                  onClick={closeCalendar}
                  className="p-2 text-gray-400 hover:text-white transition-colors rounded-lg hover:bg-dark-700/50"
                  title="Close (ESC)"
                >
                  <SafeIcon icon={FiX} className="w-6 h-6" />
                </button>
              </div>

              {/* Calendar Content */}
              <div className="h-[600px] bg-gradient-to-br from-dark-800 to-dark-900">
                <iframe
                  src="https://cal.com/guychenya?embed=true"
                  className="w-full h-full border-0"
                  allow="camera;microphone;geolocation"
                  title="Schedule a Call with Reliabotix"
                />
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-dark-700/30 bg-dark-800/50">
                <p className="text-center text-gray-400 text-sm">
                  Press <kbd className="px-2 py-1 bg-dark-600/50 text-gray-300 rounded text-xs">ESC</kbd> to close
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Enhanced Pyrotechnic Background */}
      <PyrotechnicBackground variant="neural" intensity="high" />

      {/* Interactive Background */}
      <InteractiveBackground mousePosition={mousePosition} />

      {/* Hero Section */}
      <section ref={heroRef} className="relative py-24 lg:py-32">
        {/* Enhanced Hero Effects */}
        <EnhancedHeroEffects />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1, ease: "easeOut" }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={heroInView ? { scale: 1, opacity: 1 } : {}}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="inline-flex items-center px-6 py-3 rounded-full glass text-sm text-gray-300 mb-8 relative overflow-hidden"
            >
              {/* Animated badge background */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-primary-500/20 via-accent-purple/20 to-primary-500/20"
                animate={{
                  x: ['-100%', '100%'],
                }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
              />
              <SafeIcon icon={FiStar} className="w-4 h-4 mr-2 text-yellow-400 relative z-10" />
              <span className="relative z-10">Empowering Innovation Across Global Enterprises, SMBs, and Entrepreneurs</span>
            </motion.div>

            <motion.h1
              className="text-6xl sm:text-7xl lg:text-8xl font-bold mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <span className="text-white relative">
                Intelligent AI for{' '}
                {/* Floating particles around text */}
                {[...Array(8)].map((_, i) => (
                  <motion.span
                    key={i}
                    className="absolute w-2 h-2 bg-primary-400 rounded-full"
                    style={{
                      left: `${Math.random() * 100}%`,
                      top: `${Math.random() * 100}%`,
                    }}
                    animate={{
                      y: [0, -20, 0],
                      opacity: [0, 1, 0],
                      scale: [0, 1, 0],
                    }}
                    transition={{
                      duration: 3,
                      delay: i * 0.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}
              </span>
              <br />
              <motion.span
                className="bg-gradient-to-r from-primary-400 via-accent-purple to-accent-cyan bg-clip-text text-transparent relative"
                animate={{
                  backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
                }}
                transition={{
                  duration: 5,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                style={{
                  backgroundSize: '200% 200%',
                }}
              >
                Reliable Results
                {/* Sparkle effects */}
                {[...Array(5)].map((_, i) => (
                  <motion.span
                    key={`sparkle-${i}`}
                    className="absolute text-yellow-400"
                    style={{
                      left: `${20 + i * 20}%`,
                      top: `${10 + (i % 2) * 80}%`,
                      fontSize: '0.5em',
                    }}
                    animate={{
                      scale: [0, 1, 0],
                      rotate: [0, 180, 360],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 2,
                      delay: i * 0.4,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  >
                    ✨
                  </motion.span>
                ))}
              </motion.span>
            </motion.h1>

            <motion.p
              className="text-xl text-gray-300 mb-12 max-w-4xl mx-auto leading-relaxed"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              Reliabotix leverages industry-leading platforms including Google AI, along with cutting-edge VibeCoding systems already established in the market. Our expertise spans the most advanced AI development platforms available today.
            </motion.p>

            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center mb-16"
              initial={{ opacity: 0, y: 20 }}
              animate={heroInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.8 }}
            >
              <motion.div
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                className="group relative"
              >
                {/* Button glow effect */}
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl blur-xl opacity-50"
                  animate={{
                    scale: [1, 1.1, 1],
                    opacity: [0.5, 0.8, 0.5],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <button
                  onClick={handleBookCall}
                  className="relative inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-2xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 glass-button overflow-hidden"
                >
                  {/* Button shine effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    animate={{
                      x: ['-100%', '100%'],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      repeatDelay: 3,
                      ease: "easeInOut",
                    }}
                  />
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 mr-3 relative z-10" />
                  <span className="relative z-10">Schedule Discovery Call</span>
                  <SafeIcon icon={FiArrowRight} className="w-5 h-5 ml-3 group-hover:translate-x-1 transition-transform relative z-10" />
                </button>
              </motion.div>

              <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
                <Link
                  to="/demos"
                  className="inline-flex items-center justify-center px-10 py-5 glass-card text-white rounded-2xl font-semibold transition-all duration-300 relative overflow-hidden group"
                >
                  {/* Hover glow effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-purple/20 opacity-0 group-hover:opacity-100"
                    transition={{ duration: 0.3 }}
                  />
                  <SafeIcon icon={FiPlay} className="w-5 h-5 mr-3 relative z-10" />
                  <span className="relative z-10">Watch Live Demo</span>
                </Link>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Value Pillars */}
      <section ref={pillarsRef} className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={pillarsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-white">
              Why Choose Reliabotix
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Built on three foundational pillars that deliver measurable business impact
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {valuePillars.map((pillar, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={pillarsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group glass-card rounded-3xl p-8 transition-all duration-500 relative overflow-hidden"
              >
                {/* Card glow effect */}
                <motion.div
                  className={`absolute inset-0 bg-gradient-to-r ${pillar.color} opacity-0 group-hover:opacity-10`}
                  transition={{ duration: 0.3 }}
                />

                <div className={`inline-flex items-center justify-center w-18 h-18 bg-gradient-to-r ${pillar.color} rounded-2xl mb-6 shadow-lg group-hover:scale-110 transition-transform duration-300 relative`}>
                  <SafeIcon icon={pillar.icon} className="w-10 h-10 text-white" />
                  {/* Icon pulse effect */}
                  <motion.div
                    className={`absolute inset-0 bg-gradient-to-r ${pillar.color} rounded-2xl`}
                    animate={{
                      scale: [1, 1.2, 1],
                      opacity: [0, 0.3, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                </div>

                <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-primary-300 transition-colors relative z-10">
                  {pillar.title}
                </h3>
                <p className="text-gray-300 leading-relaxed mb-6 relative z-10">{pillar.description}</p>
                <div className="glass-button text-sm text-primary-300 font-semibold px-4 py-2 rounded-lg inline-block relative z-10">
                  {pillar.proof}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Use Cases */}
      <section ref={useCasesRef} className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={useCasesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-20"
          >
            <h2 className="text-5xl lg:text-6xl font-bold mb-6 text-white">
              What We Build
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              From intelligent automation to predictive analytics, we create solutions that drive real business transformation
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                animate={useCasesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                whileHover={{ y: -8, scale: 1.02 }}
                className="group glass-card rounded-2xl p-6 transition-all duration-500 relative overflow-hidden"
              >
                {/* Card sparkle effect */}
                {[...Array(3)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-primary-400 rounded-full opacity-0 group-hover:opacity-100"
                    style={{
                      left: `${20 + i * 30}%`,
                      top: `${10 + i * 20}%`,
                    }}
                    animate={{
                      scale: [0, 1, 0],
                      opacity: [0, 1, 0],
                    }}
                    transition={{
                      duration: 1.5,
                      delay: i * 0.2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                ))}

                <div className={`inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r ${useCase.gradient} rounded-xl mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300 relative`}>
                  <SafeIcon icon={useCase.icon} className="w-7 h-7 text-white" />
                </div>

                <h3 className="text-lg font-bold text-white mb-3 group-hover:text-primary-300 transition-colors">
                  {useCase.title}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed mb-4">{useCase.description}</p>
                <div className="glass-button text-xs text-green-300 font-semibold px-3 py-1 rounded-lg inline-block">
                  {useCase.metric}
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-16">
            <motion.div whileHover={{ scale: 1.05 }}>
              <Link
                to="/solutions"
                className="group inline-flex items-center text-primary-400 hover:text-primary-300 font-semibold text-xl glass-button px-6 py-3 rounded-xl relative overflow-hidden"
              >
                <motion.div
                  className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-purple/20 opacity-0 group-hover:opacity-100"
                  transition={{ duration: 0.3 }}
                />
                <span className="relative z-10">Explore All Solutions</span>
                <SafeIcon icon={FiArrowRight} className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform relative z-10" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Enhanced CTA Section */}
      <section className="py-24 relative">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="glass-strong rounded-4xl p-16 relative overflow-hidden">
            {/* Dynamic background pattern */}
            <motion.div
              className="absolute inset-0 opacity-10"
              animate={{
                backgroundPosition: ['0% 0%', '100% 100%'],
              }}
              transition={{
                duration: 20,
                repeat: Infinity,
                ease: "linear",
              }}
              style={{
                backgroundImage: `
                  radial-gradient(circle at 25% 25%, rgba(14,165,233,0.3) 0%, transparent 50%),
                  radial-gradient(circle at 75% 75%, rgba(139,92,246,0.3) 0%, transparent 50%)
                `,
                backgroundSize: '200% 200%',
              }}
            />

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="relative z-10"
            >
              <h2 className="text-5xl lg:text-6xl font-bold mb-8 text-white">
                Ready to Transform Your Business?
              </h2>
              <p className="text-xl text-gray-300 mb-12 max-w-3xl mx-auto">
                Join industry leaders who trust Reliabotix to deliver intelligent automation and measurable results using proven AI platforms
              </p>

              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <motion.div
                  whileHover={{ scale: 1.05, y: -2 }}
                  whileTap={{ scale: 0.95 }}
                  className="relative"
                >
                  {/* Button energy effect */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-primary-500 to-primary-600 rounded-2xl blur-lg opacity-50"
                    animate={{
                      scale: [1, 1.1, 1],
                      opacity: [0.5, 0.8, 0.5],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: "easeInOut",
                    }}
                  />
                  <button
                    onClick={handleBookCall}
                    className="relative inline-flex items-center justify-center px-10 py-5 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-2xl font-semibold hover:from-primary-600 hover:to-primary-700 transition-all duration-300 shadow-lg shadow-primary-500/25 hover:shadow-primary-500/40 glass-button"
                  >
                    <SafeIcon icon={FiTrendingUp} className="w-6 h-6 mr-3" />
                    Start Your AI Journey
                  </button>
                </motion.div>

                <motion.div whileHover={{ scale: 1.05, y: -2 }} whileTap={{ scale: 0.95 }}>
                  <Link
                    to="/demos"
                    className="inline-flex items-center justify-center px-10 py-5 glass-card text-white rounded-2xl font-semibold transition-all duration-300 relative overflow-hidden group"
                  >
                    <motion.div
                      className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-purple/20 opacity-0 group-hover:opacity-100"
                      transition={{ duration: 0.3 }}
                    />
                    <SafeIcon icon={FiPlay} className="w-6 h-6 mr-3 relative z-10" />
                    <span className="relative z-10">See It In Action</span>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;